import { Component, inject, input, OnInit, output, signal } from '@angular/core';
import { finalize, Subject } from 'rxjs';
import { FormControl, FormGroup } from '@angular/forms';
import { BaseComponent } from '@app/components/base/base.component';
import { ActivatedRoute } from '@angular/router';
import { MenusServices } from '@app/services/menus.service';
import { NotificationService } from '@app/services/notifications.service';
import { InsuranceService } from '@app/services/insurance.service';
import { CreateCoverage, DealerBranchInsurance, GetCoverage } from '@app/pages/quote/insurance/insurance-model';
import { configuration, coverages, insurers } from '@app/pages/quote/standard-quote/model/standard-quote.model';

interface InsuranceConfig {
  [key: string]: {
    logo: string;
    id: number;
  };
}

const insuranceConfig: InsuranceConfig = {
  Qualitas: {
    logo: './assets/images/insurance/logo_qualitas.png',
    id: 222,
  },
  'Grupo Nacional Provincial': {
    logo: './assets/images/insurance/logo_gnp_seguros.png',
    id: 14625,
  },
  'HDI SEGUROS, S. A. DE C. V.': {
    logo: './assets/images/insurance/logo_hdi_seguros.png',
    id: 240819,
  },
};

@Component({
  template: '',
})
export class InsuranceFlexWrapper extends BaseComponent implements OnInit {
  // SERVICES
  private readonly insuranceService = inject(InsuranceService);

  // OUTPUTS
  readonly closed = output<any>();

  // INPUTS
  readonly insurance = input<any>();
  readonly dealerBranch = input<DealerBranchInsurance>();

  // VARIABLES
  isLoading = signal(false);
  configuration: configuration; //listado de configuracion para generar cotizacion
  quotes: any[] = []; //listado de cotizaciones
  coverages: coverages[] = []; //listado de coberturas
  insurers: insurers[] = [];

  selectedInsuranceSubject: Subject<any> = new Subject<any>();

  currentStep: number = 1;

  forms: FormGroup[] = [];
  modelQuoterPanel: any;
  requestId: number;

  constructor(
    route: ActivatedRoute,
    menu: MenusServices,
    notification: NotificationService
  ) {
    super(route, menu, notification);
  }

  ngOnInit() {
    this.getCoverage();
  }

  //#region Get-Coverage
  createForm() {
    this.configuration.coverages.map((coverage) => {
      const formGroup = new FormGroup({
        coverageId: new FormControl(coverage.coverageId),
        filterValueRangeSum: new FormControl({
          value:
            coverage.modelRange.rangesSums.length == 1 &&
            !coverage.special &&
            !coverage.isMaskingSumsInsured
              ? coverage.modelRange.rangesSums[0]
              : coverage.filterValueRangeSum,
          disabled: !coverage.selected,
        }),
        filterValueRangeDeductible: new FormControl({
          value:
            coverage.modelRange.deductibleRanges.length == 1 &&
            !coverage.special
              ? coverage.modelRange.deductibleRanges[0]
              : coverage.filterValueRangeDeductible,
          disabled: !coverage.selected,
        }),
        selected: new FormControl(coverage.selected),
      });
      if (!coverage.fixedProfile) formGroup.controls['selected'].disable();
      this.forms.push(formGroup);
    });
  }

  getCoverage() {
    //Obtengo y formo resultado de coberturas
    this.isLoading.set(true);
    // console.log("getCoverage", { insurance: this.insurance(), insuranceParams: this.insuranceService.insuranceParams(), insuranceModel: this.insuranceService.insuranceModel() });
    let body = new GetCoverage(
      this.insurance(),
      this.insuranceService.insuranceParams()!,
      this.insuranceService.insuranceModel()!
    );
    this.insuranceService.getCoverage(body).pipe(finalize(() => this.isLoading.set(false))).subscribe({
      next: (response) => {
        if (response.status) {
          this.configurationModel(response);
        } else {
          this.handleMarshErrors(response);
          this.isLoading.set(false);
          this.onClose();
        }
      },
      error: (error) => {
        this.isLoading.set(false);
      },
    });
  }

  configurationModel(response: any) {
    let modelquoterPanel = response.data.modelQuoterPanel;
    modelquoterPanel.stateId = this.insurance()?.stateId;
    modelquoterPanel = this.normalizeInitialPanel(modelquoterPanel);
    let model = {
      productId: modelquoterPanel.productId,
      insurers: modelquoterPanel.insurers,
      coverages: modelquoterPanel.coverages.map((i: any) => ({
        coverageId: i.coverageId,
        coverageName: i.coverageName,
        modelRange: {
          deductibleRanges: i.modelRange.deductibleRanges,
          rangesSums: i.modelRange.rangesSums,
        },
        insurersCoverage: i.insurersCoverage.map((i: any) => ({
          insurerId: i.insurerId,
          insurerName: i.insurerName,
          coverageId: i.coverageId,
          coverageName: i.coverageName,
          coverageIndicator: i.coverageIndicator,
          modelRanges: i.modelRanges,
          filterValueRangeSum: i.filterValueRangeSum,
          filterValueRangeDeductible: i.filterValueRangeDeductible,
        })),
        filterValueRangeSum: i.filterValueRangeSum,
        filterValueRangeDeductible: i.filterValueRangeDeductible,
        sumInsuredDefault: i.sumInsuredDefault,
        maskingSumsInsured: i.maskingSumsInsured,
        defaultDeductible: i.defaultDeductible,
        fixed: i.fixed, // saber si la cobertura es opcional o no
        fixedProfile: i.fixedProfile, //Saber si el checkbox esta deshabilitado
        selected: i.selected, //Saber si estara seleccionado el checkbox
        isMaskingSumsInsured: i.isMaskingSumsInsured, //Booleano para saber si es de tipo Amparado
        special: i.special, //booleano para saber si es un campo especial
        isMaskingDeductibles: i.isMaskingDeductibles, //boolean utilizado para saber si es de tip S/D
        dependency: i.dependency,
        relatedCoverage: i.relatedCoverage,
      })),
    };
    this.modelQuoterPanel = modelquoterPanel;
    this.configuration = model;
    this.insurers = this.configuration.insurers;
    //search by key from insurance name
    this.insurers.map((insurer) => {
      insurer.src = insuranceConfig[insurer.insurerName]
        ? insuranceConfig[insurer.insurerName].logo
        : '';
      insurer.alt = insurer.insurerName;
    });

    this.coverages = this.configuration.coverages;
    this.createForm();
  }

  isNumberString(value: string): boolean {
    // Expresión regular para verificar si la cadena es un número
    const numberPattern = /^[0-9]*$/;
    return numberPattern.test(value);
  }

  getModel() {
    //Obtengo valores del formulario para cada cobertura
    const model: any[] = [];
    this.forms.map((form) => {
      model.push(form.getRawValue());
    });
    let coverages = this.modelQuoterPanel.coverages.map((i: any) => {
      let coverageValue = model.find((m) => m.coverageId == i.coverageId);
      return {
        ...i,
        filterValueRangeSum:
          i.coverageId == coverageValue.coverageId
            ? coverageValue.filterValueRangeSum
            : i.filterValueRangeSum,
        filterValueRangeDeductible:
          i.coverageId == coverageValue.coverageId
            ? coverageValue.filterValueRangeDeductible
            : i.filterValueRangeDeductible,
        selected:
          i.coverageId == coverageValue.coverageId
            ? coverageValue.selected
            : i.selected,
      };
    });
    this.modelQuoterPanel.coverages = coverages;
    return this.modelQuoterPanel;
  }

  getCoverageIndicator(coverageId: any, insurerId: any): string {
    let coverageIndicator = this.coverages
      .find((coverage: any) => coverage.coverageId === coverageId)
      ?.insurersCoverage.find(
        (i) => i.insurerId == insurerId
      ).coverageIndicator;

    return coverageIndicator;
  }

  //#endregion

  updateFilterRangesSums(event: any, coverageId: number, insurerId: number) {
    if (event) {
      const value = (event.target as HTMLSelectElement).value;
      let panel = this.getModel();
      panel = {
        ...panel,
        coverages: panel.coverages.map((i: any) => {
          return {
            ...i,
            insurersCoverage:
              i.coverageId == coverageId
                ? i.insurersCoverage.map((i: any) => {
                    return {
                      ...i,
                      filterValueRangeSum:
                        i.insurerId == insurerId
                          ? value
                          : i.filterValueRangeSum,
                    };
                  })
                : i.insurersCoverage,
          };
        }),
      };

      this.recalculateCoverage(panel);
    }
  }

  updateFilterDeductibleRanges(
    event: any,
    coverageId: number,
    insurerId: number
  ) {
    if (event) {
      const value = (event.target as HTMLSelectElement).value;
      let panel = this.getModel();

      panel = {
        ...panel,
        coverages: panel.coverages.map((i: any) => {
          return {
            ...i,
            insurersCoverage:
              i.coverageId == coverageId
                ? i.insurersCoverage.map((i: any) => {
                    return {
                      ...i,
                      filterValueRangeDeductible:
                        i.insurerId == insurerId
                          ? value
                          : i.filterValueRangeDeductible,
                    };
                  })
                : i.insurersCoverage,
          };
        }),
      };

      this.recalculateCoverage(panel);
    }
  }

  getRecalculateCoverage(coverage: any = null, index: number) {
    if (coverage != null && coverage.special && coverage.dependency != '1') {
    } else {
      const bodyRecalculate = this.getModel();
      this.recalculateCoverage(bodyRecalculate);
    }
  }

  recalculateCoverage(panel: any) {
    this.isLoading.set(true);
    this.forms = [];
    this.insuranceService.getRecalculateCoverage(panel).subscribe({
      next: (response) => {
        if (response.status) {
          this.configurationModel(response);
          this.isLoading.set(false);
        } else {
          this.showNotificationError(
            'Ha ocurrido un error. Vuelva a intentarlo.'
          );
          this.onClose();
          this.isLoading.set(false);
        }
      },
      error: (error) => {
        this.isLoading.set(false);
      },
    });
  }

  validateCoverage() {
    let errors = new Array<any>();
    this.coverages.forEach((coverage, index) => {
      if (coverage.dependency != '1' && coverage.special) {
        const form = this.forms[index];
        if (this.forms[index].controls['selected'].value) {
          if (!form.controls['filterValueRangeDeductible'].value) {
            errors.push({
              coverage: coverage.coverageName,
              field: 'Deducible',
              message: 'Favor de validar el campo Deducible',
            });
          } else {
            if (
              !this.isNumberString(
                form.controls['filterValueRangeDeductible'].value
              )
            ) {
              errors.push({
                coverage: coverage.coverageName,
                field: 'Deducible',
                message: 'Favor de ingresar un valor numérico',
              });
            }
          }
          if (!form.controls['filterValueRangeSum'].value) {
            errors.push({
              coverage: coverage.coverageName,
              field: 'Sumas Aseguradas',
              message: 'Favor de validar el campo Sumas Aseguradas',
            });
          }
        }
      }
    });
    return errors;
  }

  showErrorCoverage(errors: any[]) {
    const seenCoverages = new Set();
    const filteredErrors = errors.filter(error => {
      if (seenCoverages.has(error.coverage)) {
        return false;
      }
      seenCoverages.add(error.coverage);
      return true;
    });

  filteredErrors.forEach((error: any) => {
      this.showNotificationError(
        'Verifique panel de Coberturas/Aseguradoras, requiere la captura de: \n' +
          'Cobertura :' +
          error.coverage +
          ' ' +
          error.field +
          ' ' +
          ' ' +
          error.message
      );
    });
  }

  //#region Create-Quote
  createQuote() {
    let errors = this.validateCoverage();
    if (errors.length > 0) {
      this.showErrorCoverage(errors);
      return;
    }
    this.createQuoteFromPanel(this.getModel());
  }

  private createQuoteFromPanel(panel: any) {
    const body = new CreateCoverage(
      this.normalizePanelForCreateQuote(panel),
      this.insurance(),
      this.dealerBranch()!,
      this.insuranceService.insuranceParams()!,
      this.insuranceService.insuranceModel()!
    );

    // console.log("create coverage body", JSON.stringify(body));

    body.panel.coverages.forEach((coverage: any) => {
      coverage.insurersCoverage.forEach((insurer: any) => {
        insurer.fixedCoverage = false;
        insurer.filterValueRangeSum = coverage.filterValueRangeSum;
        insurer.filterValueRangeDeductible =
          coverage.filterValueRangeDeductible;
      });
    });
    //console.log("acreate coverage body", JSON.stringify(body));
    this.isLoading.set(true);
    this.insuranceService.createQuote(body).subscribe({
      next: (response) => {
        if (response.status) {
          this.currentStep = 2;
          this.requestId = response.data.applicationId;
          this.getCoverageQuote(response.data.applicationId);
        } else {
          this.handleMarshErrors(response);
          this.onClose();
          this.isLoading.set(false);
        }
      },
      error: (error) => {
        this.isLoading.set(false);
      },
    });
  }

  private normalizePanelForCreateQuote(panel: any): any {
    const flexProduct = this.getFlexProduct(panel);
    return {
      ...panel,
      coverages: this.normalizeCoveragesForCreateQuote(panel?.coverages),
      flexProduct,
      flexProductId: panel?.flexProductId || flexProduct,
      subBrand: this.getMarshSubBrand(panel),
      typeLoad: this.getMarshLoadType(panel),
      udiList: null,
    };
  }

  private normalizeCoveragesForCreateQuote(coverages: any[]): any[] {
    if (!Array.isArray(coverages)) return coverages;
    return coverages.map((coverage: any) => ({
      ...coverage,
      insurersCoverage: Array.isArray(coverage?.insurersCoverage)
        ? coverage.insurersCoverage.map((insurer: any) => {
            if (coverage?.coverageId === 3296 && insurer?.insurerId === 222) {
              return { ...insurer, coverageIndicator: 'NA' };
            }
            return insurer;
          })
        : coverage?.insurersCoverage,
    }));
  }

  private normalizeInitialPanel(panel: any): any {
    const flexProduct = this.getFlexProduct(panel);
    return {
      ...panel,
      flexProduct,
      flexProductId: panel?.flexProductId || flexProduct,
      subBrand: this.getMarshSubBrand(panel),
      typeLoad: this.getMarshLoadType(panel),
    };
  }

  private getFlexProduct(panel: any): any {
    return panel?.flexProduct || panel?.flexProductId;
  }

  private getMarshSubBrand(panel: any): string | undefined {
    const insuranceModel = this.insuranceService.insuranceModel();
    if (insuranceModel?.modelType === 'TRACTS') return 'TRACTOCAMION';
    if (panel?.subBrand) return panel.subBrand;
    return insuranceModel?.subBrand?.name;
  }

  private getMarshLoadType(panel: any): string | null {
    return this.insurance()?.loadType ?? panel?.typeLoad ?? null;
  }

  //#endregion

  //#region Error Handling

  private handleMarshErrors(response: any) {
    console.warn('Marsh API Error Response:', response);

    if (!response || (!response.errors && !response.error)) {
      this.showDefaultError();
      return;
    }

    if (response.errors || response.error) {
      const rawErrors = response.errors ?? response.error;
      const errors = Array.isArray(rawErrors)
        ? rawErrors
        : rawErrors
        ? [rawErrors]
        : [];
      for (const errorString of errors) {
        if (typeof errorString === 'string' &&
            errorString.includes("IdField=IdTipoVehiculo") &&
            errorString.includes("Field=Tipo de Unidad")) {

          const modelId = this.insuranceService.insuranceModel()?.modelCode;
          this.showNotificationError(
            `No es posible generar una cotización de seguro para la unidad con ID ${modelId}. Verifique que los datos sean correctos.`
          );
          return;
        }
        const clean = errorString.replace(/^\[\]/, '');
        if(clean && clean.includes("sobrepasa el monto permitido")){
          this.showNotificationError(clean);
          return;
        }
      }
    }

    this.showDefaultError();
  }

  private showDefaultError(): void {
    const defaultMessage = 'Se ha producido un error al generar la cotización. Por favor, inténtelo nuevamente más tarde o intente modificar la configuración de la cotización.';
    this.showNotificationError(defaultMessage);
  }

  //#endregion

  //#region Get-Quote

  getCoverageQuote(id: number) {
    this.isLoading.set(true);
    this.insuranceService.getQuoteCoverage(id).subscribe({
      next: (response) => {
        if (response.status) {
          this.quotes = response.data.productInsurers.map((i: any) => ({
            insuranceId: i.insuranceId,
            productId: i.productId,
            packageList: i.packageList[0],
          }));
          this.isLoading.set(false);
        } else {
          this.handleMarshErrors(response);
          this.onClose();
          this.isLoading.set(false);
        }
      },
      error: (error) => {
        this.isLoading.set(false);
      },
    });
  }

  getListCoverage() {
    this.quotes.forEach((i) => {
      if (i.packageList && i.packageList.coverageListModel) {
        i.packageList.coverageListModel.map((coverage: any) => {
          const existingCoverage = this.coverages.find(
            (c: any) => c.coverageId === coverage.coverageId
          );
          if (!existingCoverage) {
            this.coverages.push(coverage);
          }
        });
      }
    });
  }

  getByCoverageId(id: any, coverageId: any): string {
    if (this.quotes.length <= 0) return 'N/A';
    const insurance = this.quotes.find(
      (insurance: any) => insurance.insuranceId == id
    );
    if (!insurance?.packageList?.coverageListModel) return 'N/A';

    let coverage = insurance.packageList.coverageListModel.find(
      (coverage: any) =>
        coverage.coverageId === coverageId ||
        (coverage.name === 'GASTOS MÉDICOS CONDUCTOR' && coverageId === 446) ||
        (coverage.name === 'GASTOS MÉDICOS' && coverageId === 2441)
    );
    return coverage ? coverage.sumAssured + ' ' + coverage.deductible : 'N/A';
  }

  selectedInsurance(insuranceId: number) {
    const packageSelected = this.quotes.find(
      (insurance: any) => insurance.insuranceId == insuranceId
    );
    const insuranceSelected = this.configuration.insurers.find(
      (insurance: any) => insurance.insurerId == insuranceId
    );
    let model = {
      insuranceId: insuranceSelected?.insurerId,
      insurerName: insuranceSelected?.insurerName,
      packageList: packageSelected.packageList,
      requestId: this.requestId,
    };
    this.selectedInsuranceSubject.next(model);
    this.onClose(model);
  }

  //#endregion

  goToPreviousStep() {
    this.currentStep = 1;
  }

  onClose(model?: any) {
    this.closed.emit(model);
  }

  existPackageList(insurerId: number) {
    const insurances = this.quotes.find(
      (insurance: any) => insurance.insuranceId == insurerId
    );
    return insurances && insurances.packageList;
  }

  getPackageLisAmount(insurerId: number) {
    const insurances = this.quotes.find(
      (insurance: any) => insurance.insuranceId == insurerId
    );
    if (!insurances) return;
    return insurances.packageList.amount;
  }

  getPackageListNumber(insurerId: number) {
    const insurances = this.quotes.find(
      (insurance: any) => insurance.insuranceId == insurerId
    );
    if (!insurances) return;
    return insurances.packageList.number;
  }
  getPackageListName(insurerId: number) {
    const insurances = this.quotes.find(
      (insurance: any) => insurance.insuranceId == insurerId
    );
    if (!insurances) return;
    return insurances.packageList.name;
  }

  // cONDICIONES PARA LAS SUMAS ASEGURADAS DE LAS ASEGURADORAS
  isIfSumOne(coverage: any) {
    return coverage.isMaskingSumsInsured && !coverage.special;
  }

  isIfSumTwo(coverage: any) {
    return coverage.modelRange?.rangesSums?.length > 1 && !coverage?.special && !coverage?.isMaskingSumsInsured;
  }

  isIfSumThree(coverage: any) {
    return coverage.modelRange?.rangesSums?.length == 1 && !coverage?.special && !coverage?.isMaskingSumsInsured;
  }

  isIfSumFour(coverage: any) {
    return coverage.special && coverage.dependency != '1';
  }

  isIfDeductOne(coverage: any) {
    return coverage.isMaskingDeductibles && !coverage.special;
  }

  // cONDICIONES PARA LOS DEDUCTIBLES DE LAS ASEGURADORAS
  isIfDeductTwo(coverage: any) {
    return coverage.modelRange?.deductibleRanges?.length > 1 && coverage?.dependency != '1' && coverage?.dependency != '2' && !coverage?.special && !coverage?.isMaskingDeductibles;
  }

  isIfDeductThree(coverage: any) {
    return coverage.modelRange?.deductibleRanges?.length == 1 && coverage?.dependency != '1' && coverage?.dependency != '2' && !coverage?.special && !coverage?.isMaskingDeductibles;
  }

  isIfDeductFour(coverage: any) {
    return coverage.special && coverage?.dependency != '1' && coverage?.dependency != '2' && !coverage?.isMaskingDeductibles;
  }

  isIfDeductFive(coverage: any) {
    return !coverage?.special && (coverage?.dependency == '1' || coverage?.dependency == '2') && !coverage?.isMaskingDeductibles && coverage?.relatedCoverage == 1;
  }

  isIfDeductSix(coverage: any) {
    return !coverage?.special && (coverage?.dependency == '1' || coverage?.dependency == '2') && !coverage?.isMaskingDeductibles && coverage?.relatedCoverage != 1;
  }

  // cONDICIONES PARA LAS ASEGURADORAS
  isCoverageChecked(insurerCoverage: any){
    return insurerCoverage?.coverageIndicator == 'CHECKED';
  }
  isCoverageNA(insurerCoverage: any){
    return insurerCoverage?.coverageIndicator == 'NA' || (insurerCoverage?.coverageIndicator == 'UNCHECKED' && insurerCoverage?.modelRanges == null);
  }
  isCoverageNAWithRanges(insurerCoverage: any){
    return insurerCoverage?.coverageIndicator == 'NA' && insurerCoverage?.modelRanges != null;
  }
  isCoverageModelRangesSumsMoreThanOne(insurerCoverage: any){
    return insurerCoverage?.modelRanges?.rangesSums && insurerCoverage?.modelRanges?.rangesSums?.length > 1;
  }
  isCoverageModelRangesDeductibleMoreThanOne(insurerCoverage: any){
    return insurerCoverage?.modelRanges?.deductibleRanges && insurerCoverage?.modelRanges?.deductibleRanges?.length > 1 && insurerCoverage?.modelRanges?.deductibleRanges == 'S/D';
  }
}
